import './QuestionForm.css';
import { useState, useEffect } from 'react';
import { searchBooks } from '../../../services/bookService';
import { searchAuthors } from '../../../services/authorService';

function LinkingForm({ onSubmit, onCancel }) {
    const [questionText, setQuestionText] = useState('');
    const [leftItems, setLeftItems] = useState([
        { id: 'left-1', text: '' },
        { id: 'left-2', text: '' },
        { id: 'left-3', text: '' }
    ]);
    const [rightItems, setRightItems] = useState([
        { id: 'right-1', text: '' },
        { id: 'right-2', text: '' },
        { id: 'right-3', text: '' }
    ]);
    const [pairs, setPairs] = useState([
        { leftId: 'left-1', rightId: 'right-1' },
        { leftId: 'left-2', rightId: 'right-2' },
        { leftId: 'left-3', rightId: 'right-3' }
    ]);

    // Combined search for books and authors
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [searchResults, setSearchResults] = useState({ books: [], authors: [] });
    const [selectedItems, setSelectedItems] = useState([]); // Array of { type: 'book'|'author', data: {...} }
    const [searching, setSearching] = useState(false);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Search both books and authors
    useEffect(() => {
        const performSearch = async () => {
            if (debouncedSearch.trim().length < 2) {
                setSearchResults({ books: [], authors: [], showUnlinked: false });
                return;
            }

            try {
                setSearching(true);

                const term = debouncedSearch.trim();
                const bookFilters = { title: term, authorName: term };
                const [bookResults, authorResults] = await Promise.all([
                    searchBooks(bookFilters, 0, 5),
                    searchAuthors(term, 0, 5)
                ]);
                const showUnlinked = 'literární teorie'.includes(term.toLowerCase());

                setSearchResults({
                    books: bookResults.content || [],
                    authors: authorResults.content || [],
                    showUnlinked
                });
            } catch (err) {
                console.error('Error searching:', err);
                setSearchResults({ books: [], authors: [], showUnlinked: false });
            } finally {
                setSearching(false);
            }
        };

        performSearch();
    }, [debouncedSearch]);

    const handleLeftItemChange = (index, value) => {
        const newItems = [...leftItems];
        newItems[index].text = value;
        setLeftItems(newItems);
    };

    const handleRightItemChange = (index, value) => {
        const newItems = [...rightItems];
        newItems[index].text = value;
        setRightItems(newItems);
    };

    const handleAddPair = () => {
        const newIndex = leftItems.length + 1;
        const newLeftId = `left-${newIndex}`;
        const newRightId = `right-${newIndex}`;

        setLeftItems([...leftItems, { id: newLeftId, text: '' }]);
        setRightItems([...rightItems, { id: newRightId, text: '' }]);
        setPairs([...pairs, { leftId: newLeftId, rightId: newRightId }]);
    };

    const handleRemovePair = (index) => {
        if (leftItems.length <= 2) {
            alert('Propojovačka musí mít alespoň 2 páry');
            return;
        }

        const leftIdToRemove = leftItems[index].id;
        const rightIdToRemove = rightItems[index].id;

        setLeftItems(leftItems.filter((_, i) => i !== index));
        setRightItems(rightItems.filter((_, i) => i !== index));
        setPairs(pairs.filter(pair =>
            pair.leftId !== leftIdToRemove && pair.rightId !== rightIdToRemove
        ));
    };

    const handleSelectBook = (book) => {
        const alreadySelected = selectedItems.some(
            item => item.type === 'book' && item.data.id === book.id
        );
        if (alreadySelected) {
            alert('Tato kniha je již vybrána');
            return;
        }
        setSelectedItems([...selectedItems, { type: 'book', data: book }]);
        setSearchTerm('');
        setSearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleSelectAuthor = (author) => {
        const alreadySelected = selectedItems.some(
            item => item.type === 'author' && item.data.id === author.id
        );
        if (alreadySelected) {
            alert('Tento autor je již vybrán');
            return;
        }
        setSelectedItems([...selectedItems, { type: 'author', data: author }]);
        setSearchTerm('');
        setSearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleSelectUnlinked = () => {
        const alreadySelected = selectedItems.some(item => item.type === 'unlinked');
        if (alreadySelected) return;
        setSelectedItems([...selectedItems, { type: 'unlinked' }]);
        setSearchTerm('');
        setSearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleRemoveSelected = (index) => {
        setSelectedItems(selectedItems.filter((_, i) => i !== index));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validation
        if (!questionText.trim()) {
            alert('Zadejte znění otázky');
            return;
        }

        const filledLeftItems = leftItems.filter(item => item.text.trim());
        const filledRightItems = rightItems.filter(item => item.text.trim());

        if (filledLeftItems.length < 2 || filledRightItems.length < 2) {
            alert('Zadejte alespoň 2 páry položek');
            return;
        }

        if (filledLeftItems.length !== filledRightItems.length) {
            alert('Počet položek v levém a pravém sloupci musí být stejný');
            return;
        }

        // Filter pairs to only include filled items
        const validPairs = pairs.filter(pair => {
            const leftExists = filledLeftItems.some(item => item.id === pair.leftId);
            const rightExists = filledRightItems.some(item => item.id === pair.rightId);
            return leftExists && rightExists;
        });

        const questionData = {
            type: 'Linking',
            questionText: questionText.trim(),
            leftItems: filledLeftItems.map(item => ({
                id: item.id,
                text: item.text.trim()
            })),
            rightItems: filledRightItems.map(item => ({
                id: item.id,
                text: item.text.trim()
            })),
            correctPairs: validPairs,
            relatedBooks: selectedItems.filter(item => item.type === 'book').map(item => item.data.id),
            relatedAuthors: selectedItems.filter(item => item.type === 'author').map(item => item.data.id)
        };

        onSubmit(questionData);
    };

    return (
        <form className="question-form" onSubmit={handleSubmit}>
            {/* Question Text */}
            <div className="form-group">
                <label htmlFor="questionText" className="form-label required">
                    Znění otázky
                </label>
                <textarea
                    id="questionText"
                    className="form-textarea"
                    rows="3"
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    placeholder="Zadejte text otázky (např. 'Přiřaďte autory k jejich dílům')..."
                    required
                />
            </div>

            {/* Pairs */}
            <div className="form-group">
                <label className="form-label required">Páry k propojení</label>
                <p className="form-hint" style={{ marginBottom: '1rem' }}>
                    Zadejte položky, které mají být správně spárovány. Levý a pravý sloupec budou při kvízu v náhodném pořadí.
                </p>
                <div className="choices-list">
                    {leftItems.map((leftItem, index) => (
                        <div key={index} style={{ marginBottom: '1rem' }}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr auto 1fr auto',
                                gap: '1rem',
                                alignItems: 'center',
                                padding: '1rem',
                                backgroundColor: '#0f3460',
                                border: '2px solid #1a1a2e',
                                borderRadius: '6px'
                            }}>
                                <input
                                    type="text"
                                    className="choice-input"
                                    value={leftItem.text}
                                    onChange={(e) => handleLeftItemChange(index, e.target.value)}
                                    placeholder={`Levý sloupec ${index + 1}`}
                                    style={{ margin: 0 }}
                                />
                                <div style={{
                                    color: '#e94560',
                                    fontSize: '1.5rem',
                                    fontWeight: 'bold'
                                }}>
                                    ↔
                                </div>
                                <input
                                    type="text"
                                    className="choice-input"
                                    value={rightItems[index]?.text || ''}
                                    onChange={(e) => handleRightItemChange(index, e.target.value)}
                                    placeholder={`Pravý sloupec ${index + 1}`}
                                    style={{ margin: 0 }}
                                />
                                {leftItems.length > 2 && (
                                    <button
                                        type="button"
                                        className="btn-remove-choice"
                                        onClick={() => handleRemovePair(index)}
                                        title="Odebrat pár"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                {leftItems.length < 8 && (
                    <button
                        type="button"
                        className="btn-add-choice"
                        onClick={handleAddPair}
                    >
                        + Přidat pár
                    </button>
                )}
            </div>

            {/* Related Books and Authors */}
            <div className="form-group">
                <label className="form-label">Související knihy a autoři (nepovinné)</label>

                {selectedItems.length > 0 && (
                    <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {selectedItems.map((item, index) => (
                            <div key={index} className="selected-item">
                                <div className="selected-item-info">
                                    <span className="selected-item-type">
                                        {item.type === 'book' ? 'Kniha' : item.type === 'unlinked' ? 'Kategorie' : 'Autor'}
                                    </span>
                                    <span className="selected-item-title">
                                        {item.type === 'book'
                                            ? item.data.bookTitle
                                            : item.type === 'unlinked'
                                            ? 'Literární teorie'
                                            : item.data.name}
                                    </span>
                                    {item.type === 'book' && item.data.author?.name && (
                                        <span className="selected-item-subtitle">
                                            {item.data.author.name}
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn-remove-selected"
                                    onClick={() => handleRemoveSelected(index)}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <input
                    type="text"
                    className="form-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Vyhledat knihu nebo autora..."
                />
                {searching && <p className="search-loading">Hledám...</p>}
                {(searchResults.books.length > 0 || searchResults.authors.length > 0 || searchResults.showUnlinked) && (
                    <div className="search-results">
                        {searchResults.showUnlinked && (
                            <div
                                className="search-result-item"
                                onClick={handleSelectUnlinked}
                            >
                                <span className="result-title">Literární teorie</span>
                                <span className="result-subtitle">Otázky o literární teorii</span>
                            </div>
                        )}
                        {searchResults.books.length > 0 && (
                            <>
                                <div className="search-results-section-header">Knihy</div>
                                {searchResults.books.map(book => (
                                    <div
                                        key={`book-${book.id}`}
                                        className="search-result-item"
                                        onClick={() => handleSelectBook(book)}
                                    >
                                        <span className="result-title">{book.bookTitle}</span>
                                        <span className="result-subtitle">{book.author?.name}</span>
                                    </div>
                                ))}
                            </>
                        )}
                        {searchResults.authors.length > 0 && (
                            <>
                                <div className="search-results-section-header">Autoři</div>
                                {searchResults.authors.map(author => (
                                    <div
                                        key={`author-${author.id}`}
                                        className="search-result-item"
                                        onClick={() => handleSelectAuthor(author)}
                                    >
                                        <span className="result-title">{author.name}</span>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Form Actions */}
            <div className="form-actions">
                <button type="submit" className="btn-submit">
                    Vytvořit otázku
                </button>
                <button type="button" className="btn-cancel-form" onClick={onCancel}>
                    Zrušit
                </button>
            </div>
        </form>
    );
}

export default LinkingForm;
